import React, { useState, useCallback } from 'react';
import prettySize from 'bytes';
import { ErrorMessage } from '@atlaskit/form';
import { useIntl } from '@atlassian/jira-intl';
import {
	FORM_DROPZONE_CONTAINER_ID,
	MINIMISED_DROPZONE_CONTAINER_ID,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import CommonAttachmentsField from '@atlassian/jira-issue-create-commons/src/ui/attachments-field/index.tsx';
import { useMinimizableModal } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/index.tsx';
import { useRefetchMediaTokenOnExpired } from '@atlassian/jira-media-token-tracker/src/services/useRefetchMediaTokenOnExpired.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useUploadPermissions } from '../../../../services/media-upload-permissions/index.tsx';
import messages from './messages.tsx';
import type { AttachmentFieldProps } from './types.tsx';

export const useAttachmentValidationState = () => useState<boolean>(false);

const AttachmentsField = (props: AttachmentFieldProps) => {
	const { view } = useMinimizableModal();

	const {
		fieldId,
		fieldName,
		description,
		isRequired,
		additionalData,
		selectedProject,
		onInProgressChange,
		additionalData: { maxAllowedTotalAttachmentsSize },
		defaultValue,
		isCollapsed,
	} = props;

	const { mediaContext } = additionalData;
	const uploadToken = mediaContext?.uploadToken;
	const { formatMessage } = useIntl();
	const [attachmentSizeValidation, onAttachmentSizeValidation] = useAttachmentValidationState();
	const [isRefetching, setIsRefetching] = useState(false);

	// Use the upload permissions service to refetch tokens (only when feature gate is enabled)
	const [isUploadPermissionFetching, uploadPermissions, fetchUploadPermissions] =
		useUploadPermissions();

	let attachmentUploadContext;

	// Use refetched permissions if available and feature gate is enabled, otherwise fall back to original token
	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('gic-attachment-media-token-refresh') && uploadPermissions) {
		attachmentUploadContext = {
			clientId: uploadPermissions.clientId,
			serviceHost: uploadPermissions.serviceHost,
			collection: uploadPermissions.collection,
			token: uploadPermissions.token,
		};
	} else if (uploadToken) {
		attachmentUploadContext = {
			clientId: uploadToken.clientId,
			serviceHost: uploadToken.endpointUrl,
			collection: uploadToken.targetCollection,
			token: uploadToken.token,
		};
	}

	// Refetch function that uses the upload permissions service (only when feature gate is enabled)
	const refetchToken = useCallback(async () => {
		if (!selectedProject?.value || isRefetching) {
			return;
		}

		setIsRefetching(true);

		try {
			// Use project-collection type for attachments field
			await fetchUploadPermissions(selectedProject.value, 'project-collection');
		} finally {
			setIsRefetching(false);
		}
	}, [selectedProject?.value, fetchUploadPermissions, isRefetching]);

	// Use the existing media token refetch hook to automatically refetch when token expires
	if (fg('gic-attachment-media-token-refresh')) {
		// Use the current token (either from uploadPermissions or uploadToken)
		const currentToken = uploadPermissions?.token ?? uploadToken?.token ?? null;

		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useRefetchMediaTokenOnExpired({
			mediaToken: currentToken,
			refetch: refetchToken,
			shouldWaitUntilBrowserActive: true,
		});
	}

	const dropzoneContainerId =
		view === 'modal' ? FORM_DROPZONE_CONTAINER_ID : MINIMISED_DROPZONE_CONTAINER_ID;

	return (
		<>
			<CommonAttachmentsField
				fieldId={fieldId}
				fieldName={fieldName}
				description={description}
				isRequired={isRequired}
				mediaUploadContext={attachmentUploadContext}
				projectId={selectedProject?.value || null}
				onInProgressChange={onInProgressChange}
				maxAllowedTotalAttachmentsSize={maxAllowedTotalAttachmentsSize}
				onAttachmentSizeValidation={onAttachmentSizeValidation}
				dropzoneContainerId={dropzoneContainerId}
				shouldScrollIntoViewOnUploadStart
				defaultValue={defaultValue}
				isCollapsed={isCollapsed}
			/>
			{!attachmentUploadContext && !isUploadPermissionFetching && (
				<ErrorMessage>{formatMessage(messages.missingToken)}</ErrorMessage>
			)}
			{attachmentSizeValidation && (
				<ErrorMessage>
					{formatMessage(messages.attachmentSizeLimitCheck)} (
					{/* @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'. */}
					{prettySize(maxAllowedTotalAttachmentsSize, {
						unitSeparator: ' ',
					})}
					).
				</ErrorMessage>
			)}
		</>
	);
};

export default AttachmentsField;
